import { useParams, useRouter } from '@/lib/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from '@/i18n';
import TopPanel from '@/components/ui/TopPanel';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import { useTrip } from '@/hooks/trips/useTrip';

import CityForm, { type CityFormData } from '@/components/trips/form/CityForm';
import CityCard from '@/components/trips/cards/CityCard';
import ExpensesForm, { type ExpensesFormData } from '@/components/trips/form/ExpensesForm';
import { useSaveCity } from '@/hooks/trips/useSaveCity';
import { isAfter, isSameDay, parseISO, subDays } from 'date-fns';
import type { TripCityDetail, TripDetailCity } from '@/hooks/trips/types';
import { useDeleteCity, DeleteCityError } from '@/hooks/trips/useDeleteCity';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog';
import { useToast } from '@/components/ui/toast';
import { getCityValidationResult, type CityForValidation } from '@/lib/city-date-validation';

function mapToCityDetail(base: TripDetailCity, fallback?: TripCityDetail | null): TripCityDetail {
  const prior = fallback && fallback.id === base.id ? fallback : null;
  const hotels =
    Array.isArray(base.hotels) && base.hotels.length > 0 ? base.hotels : (prior?.hotels ?? []);
  const transportations =
    Array.isArray(base.transportations) && base.transportations.length > 0
      ? base.transportations
      : (prior?.transportations ?? []);
  const fuels =
    Array.isArray(base.fuels) && base.fuels.length > 0 ? base.fuels : (prior?.fuels ?? []);

  return {
    ...base,
    hotels,
    transportations,
    fuels,
  };
}

export function CityPage() {
  const params = useParams();
  const router = useRouter();
  const tripId = params.id as string;
  const cityIdParam = params.cityId as string;

  const t = useTranslations('cityForm');
  const tTripForm = useTranslations('tripForm');
  const tTrips = useTranslations('trips');
  const tDeleteCityDialog = useTranslations('trips.deleteCityDialog');
  const tNav = useTranslations('navigation');
  const tToasts = useTranslations('toasts');
  const { showToast } = useToast();

  const { data: trip, isLoading: tripLoading, isFetching: tripFetching } = useTrip(tripId);
  const saveCityMutation = useSaveCity();

  // Determine if this is a new city or existing one
  const isNewCity = cityIdParam === 'new';
  const numericCityId = !isNewCity ? Number(cityIdParam) : NaN;
  const currentCityId = !isNewCity && !Number.isNaN(numericCityId) ? numericCityId : null;

  // Fetch the specific city if we have an ID
  const tripCities = useMemo(() => trip?.cities ?? [], [trip]);
  const hasAnyCity = tripCities.length > 0;
  const isLoading = tripLoading;

  const [isEditing, setIsEditing] = useState(isNewCity);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [optimisticCity, setOptimisticCity] = useState<TripCityDetail | null>(null);
  const deleteCityMutation = useDeleteCity(tripId);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [hasPendingExpenseForm, setHasPendingExpenseForm] = useState(false);
  const expensesSectionRef = useRef<HTMLDivElement | null>(null);

  const cityDateRanges = useMemo(() => {
    return tripCities.map((city) => {
      // Find the latest hotel checkout date for this city
      const latestHotelCheckout =
        city.hotels?.length > 0
          ? city.hotels.reduce((latest: Date | null, hotel) => {
              const checkOut = parseISO(hotel.checkOutDate);
              return !latest || isAfter(checkOut, latest) ? checkOut : latest;
            }, null)
          : null;

      return {
        id: city.id,
        arrivalDate: parseISO(city.arrivalDate),
        departureDate: parseISO(city.departureDate),
        hotelCheckoutDate: latestHotelCheckout,
      };
    });
  }, [tripCities]);

  const otherCityRanges = useMemo(() => {
    if (currentCityId) {
      return cityDateRanges.filter((range) => range.id !== currentCityId);
    }
    return cityDateRanges;
  }, [cityDateRanges, currentCityId]);

  // Check if this city can have same-day check-in/check-out for accommodation
  // Allowed if:
  // - Previous city is a single-day city (regardless of hotel), OR
  // - Previous city has a hotel with checkout connecting to this city's arrival
  const allowSameDayCheckout = useMemo(() => {
    const currentCity = cityDateRanges.find((city) => city.id === currentCityId);
    if (!currentCity) {
      return false;
    }

    // Find the city that ends the day before this city's arrival
    const dayBeforeArrival = subDays(currentCity.arrivalDate, 1);
    const previousCity = otherCityRanges.find((city) =>
      isSameDay(city.departureDate, dayBeforeArrival)
    );

    if (!previousCity) {
      return false;
    }

    // If previous city is a single-day city, allow same-day checkout regardless of previous hotel
    const isPreviousCitySingleDay = isSameDay(previousCity.arrivalDate, previousCity.departureDate);
    if (isPreviousCitySingleDay) {
      return true;
    }

    // For multi-day previous city, check if hotel connects
    if (!previousCity.hotelCheckoutDate) {
      return false;
    }

    // Check if previous city's hotel checkout connects to this city's arrival
    return isSameDay(previousCity.hotelCheckoutDate, dayBeforeArrival);
  }, [cityDateRanges, currentCityId, otherCityRanges]);

  const currentCityFromTrip = useMemo(() => {
    if (!currentCityId) {
      return null;
    }

    return tripCities.find((city) => city.id === currentCityId) ?? null;
  }, [currentCityId, tripCities]);

  // Use availableActions from API - allow editing if user can submit/resubmit the trip
  const availableActions = trip?.availableActions ?? [];
  const canSubmit = availableActions.includes('submit') || availableActions.includes('resubmit');
  const canModifyCities = canSubmit;

  // Compute date validation for current city
  const currentCityValidation = useMemo(() => {
    if (
      !currentCityId ||
      !tripCities.length ||
      !trip?.startDate ||
      !trip?.endDate ||
      !canModifyCities
    ) {
      return null;
    }

    const citiesForValidation: CityForValidation[] = tripCities.map((city) => ({
      id: city.id,
      cityName: city.cityName,
      arrivalDate: city.arrivalDate,
      departureDate: city.departureDate,
    }));

    return getCityValidationResult(
      currentCityId,
      citiesForValidation,
      trip.startDate,
      trip.endDate
    );
  }, [currentCityId, tripCities, trip?.startDate, trip?.endDate, canModifyCities]);

  const hasArrivalDateIssue = currentCityValidation?.hasArrivalIssue ?? false;
  const hasDepartureDateIssue = currentCityValidation?.hasDepartureIssue ?? false;

  const getDeleteErrorMessage = (error: unknown) => {
    if (error instanceof DeleteCityError && error.status === 403) {
      return tTrips('deleteCityError.forbidden');
    }

    if (error instanceof Error && error.message) {
      return error.message;
    }

    return tTrips('deleteCityError.generic');
  };

  const closeDeleteDialog = () => {
    if (deleteCityMutation.isPending) {
      return;
    }
    setShowDeleteDialog(false);
    setDeleteError(null);
    deleteCityMutation.reset();
  };

  const confirmDeleteCity = async () => {
    if (!currentCityId) {
      return;
    }

    setDeleteError(null);

    try {
      await deleteCityMutation.mutateAsync(currentCityId);
      closeDeleteDialog();
      router.push(`/trips/${tripId}`);
    } catch (error) {
      setDeleteError(getDeleteErrorMessage(error));
    }
  };

  const showCityForm = canModifyCities && (isNewCity || isEditing);

  useEffect(() => {
    if (!isLoading) {
      setInitialLoadComplete(true);
    }
  }, [isLoading]);

  useEffect(() => {
    if (currentCityFromTrip) {
      setOptimisticCity(null);
    }
  }, [currentCityFromTrip]);

  const effectiveCityDetail: TripCityDetail | null = useMemo(() => {
    if (currentCityId == null) {
      return optimisticCity;
    }

    const fallback = optimisticCity && optimisticCity.id === currentCityId ? optimisticCity : null;

    if (currentCityFromTrip) {
      return mapToCityDetail(currentCityFromTrip, fallback ?? undefined);
    }

    return fallback;
  }, [currentCityFromTrip, currentCityId, optimisticCity]);

  // Convert current city to form data format
  const currentCityFormData: CityFormData | null = effectiveCityDetail
    ? {
        countryId: String(effectiveCityDetail.countryId),
        countryName: effectiveCityDetail.countryName,
        cityId: String(effectiveCityDetail.cityId),
        cityName: effectiveCityDetail.cityName,
        arrivalDate: parseISO(effectiveCityDetail.arrivalDate),
        departureDate: parseISO(effectiveCityDetail.departureDate),
        needsAccommodation: effectiveCityDetail.needHotel,
        needsTransport:
          trip?.tripType === 'local' && effectiveCityDetail.needFuel
            ? false
            : effectiveCityDetail.needTransportation,
        needsFuel: trip?.tripType !== 'international' ? effectiveCityDetail.needFuel : false,
      }
    : null;

  const allowFuel = trip?.tripType !== 'international';
  const tripStartDate = trip?.startDate ? parseISO(trip.startDate) : null;
  const tripEndDate = trip?.endDate ? parseISO(trip.endDate) : null;
  const currentExpenses: ExpensesFormData | null = effectiveCityDetail
    ? {
        accommodation: effectiveCityDetail.hotels.map((hotel) => ({
          id: hotel.id,
          checkInDate: parseISO(hotel.checkInDate),
          checkOutDate: parseISO(hotel.checkOutDate),
          totalAmount: hotel.totalAmount.toString(),
          currency: 'GEL',
          needsBookingHelp: hotel.bookingSupport,
        })),
        transport: effectiveCityDetail.transportations.map((transportation) => ({
          id: transportation.id,
          amount:
            typeof transportation.amount === 'number' && transportation.amount !== 0
              ? transportation.amount.toString()
              : '',
          currency: 'GEL',
          assistanceType: transportation.assistanceType ?? 'ticket_or_advance',
          needsBookingSupport: transportation.bookingSupport,
          arrivalDate: parseISO(effectiveCityDetail.arrivalDate),
          departureDate: parseISO(effectiveCityDetail.departureDate),
        })),
        fuel: allowFuel
          ? effectiveCityDetail.fuels.map((fuel) => ({
              id: fuel.id,
              fuelTypeId: String(fuel.fuelTypeId),
              fuelTypeName: fuel.fuelTypeName,
              carModel: fuel.carModel || '',
              amount: fuel.fuelAmount.toString(),
              currency: 'GEL',
            }))
          : [],
      }
    : null;

  const handleSave = async (data: CityFormData) => {
    try {
      // Save to backend with current expenses from React Query
      const savedCity = await saveCityMutation.mutateAsync({
        tripId,
        cityData: data,
        cityId: currentCityId || undefined,
      });

      setOptimisticCity((previous) =>
        mapToCityDetail(savedCity, currentCityFromTrip ?? previous ?? null)
      );

      // Replace URL with the new city ID and exit edit mode
      if (isNewCity) {
        router.replace(`/trips/${tripId}/cities/${savedCity.id}`);
      }
      setIsEditing(false);
      setTimeout(() => {
        expensesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);

      console.log('Saved city with ID:', savedCity.id);
    } catch (error) {
      console.error('Failed to save city:', error);
    }
  };

  useEffect(() => {
    if (!canModifyCities) {
      setIsEditing(false);
    }
  }, [canModifyCities]);

  useEffect(() => {
    if (!isLoading && isNewCity && !canModifyCities) {
      router.replace(`/trips/${tripId}`);
    }
  }, [isLoading, isNewCity, canModifyCities, router, tripId]);

  const handleEdit = () => {
    if (!canModifyCities) {
      return;
    }
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (isNewCity) {
      // If it's a new city, go back to trip page
      router.push(`/trips/${tripId}`);
    } else {
      // If editing existing city, just exit edit mode
      setIsEditing(false);
    }
  };

  const handleSaveAndContinue = () => {
    if (hasPendingExpenseForm) {
      showToast({ message: tToasts('saveExpenses'), variant: 'error' });
      return;
    }

    // Cities are already saved to backend via React Query
    console.log('Cities saved, returning to trip details');
    console.log('Saving expenses:', currentExpenses);
    router.push(`/trips/${tripId}`);
  };

  const handleDelete = () => {
    if (!currentCityId || !canModifyCities) {
      return;
    }
    deleteCityMutation.reset();
    setDeleteError(null);
    setShowDeleteDialog(true);
  };

  if (!initialLoadComplete && isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <Spinner variant="circle" />
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-white">
        {/* Fixed TopPanel */}
        <div className="fixed top-0 right-0 left-0 z-50">
          <TopPanel />
        </div>

        <main className="bg-white pt-[5.875rem] pb-24">
          <div className="relative z-20 mx-auto max-w-[76.9375rem] px-4 py-[2rem] sm:px-8 sm:py-[2.62rem]">
            {/* Page Title Section with Delete Button */}
            <div className="flex flex-col gap-4 pb-[2rem] sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h1 className="mb-2 text-[2rem] leading-[2.5rem] font-medium text-[#292929]">
                  {t('breadcrumb')}
                </h1>
                <p className="text-base leading-6 text-[#666666]">
                  {tTripForm('tripNumber')} -{' '}
                  <span className="font-medium text-[#292929]">{trip?.number || '...'}</span>
                </p>
              </div>

              {/* Delete Button - only show for existing cities */}
              {currentCityId && canModifyCities && (
                <Button
                  onClick={handleDelete}
                  variant="unstyled"
                  size="none"
                  disabled={deleteCityMutation.isPending}
                  className="flex items-center gap-2 rounded-[2rem] bg-[#ff66660a] px-4 py-2.5 pl-2.5 transition-colors hover:bg-[#ff666614] disabled:opacity-60"
                >
                  <div className="flex h-5 w-5 shrink-0 items-center justify-center">
                    <img src="/assets/icons/delete.svg" alt="" className="h-5 w-5" />
                  </div>
                  <span className="text-sm leading-5 text-[#e5484d]">{t('deleteCity')}</span>
                </Button>
              )}
            </div>

            {/* Main Content */}
            <div className="flex flex-col gap-9 lg:flex-row">
              {/* Left Column - City Details Form or Card */}
              <div className="w-full max-w-[36.375rem] lg:w-[36.375rem]">
                {showCityForm ? (
                  <CityForm
                    onSave={handleSave}
                    onCancel={handleCancel}
                    initialData={currentCityFormData || undefined}
                    isLoading={saveCityMutation.isPending}
                    tripType={trip?.tripType}
                    tripStartDate={tripStartDate}
                    tripEndDate={tripEndDate}
                    otherCities={otherCityRanges}
                    permanentResidenceCountryId={trip?.permanentResidenceCountryId ?? null}
                    permanentResidenceCityId={trip?.permanentResidenceCityId ?? null}
                    hasArrivalDateIssue={hasArrivalDateIssue}
                    hasDepartureDateIssue={hasDepartureDateIssue}
                  />
                ) : currentCityFormData ? (
                  <CityCard
                    city={currentCityFormData}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    isDeleting={deleteCityMutation.isPending}
                    canEdit={canModifyCities}
                    canDelete={canModifyCities}
                    tripType={trip?.tripType}
                    hasArrivalDateIssue={hasArrivalDateIssue}
                    hasDepartureDateIssue={hasDepartureDateIssue}
                  />
                ) : (
                  <div className="flex h-32 items-center justify-center">
                    <div className="text-center">
                      <p className="text-lg text-gray-500">{t('error')}</p>
                      <p className="text-sm text-gray-400">{t('cityLoadError')}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column - Expenses */}
              <div ref={expensesSectionRef} className="w-full flex-1 lg:w-auto">
                <ExpensesForm
                  tripId={tripId}
                  cityId={currentCityId ?? effectiveCityDetail?.id ?? undefined}
                  city={currentCityFormData}
                  tripType={trip?.tripType}
                  initialData={currentExpenses || undefined}
                  isBusy={saveCityMutation.isPending || tripFetching}
                  onFormStateChange={setHasPendingExpenseForm}
                  allowSameDayCheckout={allowSameDayCheckout}
                />
              </div>
            </div>
          </div>

          {/* Corner Logo - kept behind page content */}
          <div className="pointer-events-none fixed right-0 bottom-0 z-10">
            <img src="/assets/corner-logo.svg" alt="" className="h-[18.75rem] w-[18.75rem]" />
          </div>

          {/* Fixed Bottom Bar */}
          <div className="fixed right-0 bottom-0 left-0 z-40">
            <div className="border-t border-[#f1f1f1] bg-white">
              {/* Desktop */}
              <div className="hidden items-center justify-between px-12 py-4 lg:flex">
                <Button
                  variant="ghost"
                  onClick={() => router.push(`/trips/${tripId}`)}
                  className="flex h-auto cursor-pointer items-center justify-center gap-1.5 rounded-[0.75rem] px-4 py-2 hover:bg-[#f9f9f9]"
                >
                  <img src="/assets/icons/chevron-left.svg" alt="" className="h-5 w-5" />
                  <span className="text-base font-medium text-black">{tNav('back')}</span>
                </Button>

                <Button
                  onClick={handleSaveAndContinue}
                  disabled={!hasAnyCity}
                  className={`flex h-11 min-w-[10rem] items-center justify-center gap-2 rounded-full px-6 text-sm font-medium transition-all ${
                    hasAnyCity
                      ? 'bg-gradient-to-r from-[#F1656A] via-[#ED2891] to-[#42419A] text-white hover:opacity-90'
                      : 'cursor-not-allowed bg-[#f9f9f9] text-[#bbbbbb]'
                  }`}
                >
                  <span className="whitespace-nowrap">{t('saveAndContinue')}</span>
                </Button>
              </div>

              {/* Mobile */}
              <div className="flex w-full min-w-0 items-center justify-between gap-2 px-4 pt-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] lg:hidden">
                <Button
                  variant="ghost"
                  onClick={() => router.push(`/trips/${tripId}`)}
                  className="flex h-10 shrink-0 items-center gap-1.5 rounded-full px-3 text-sm font-medium text-black hover:bg-[#f1f1f1]"
                >
                  <img src="/assets/icons/chevron-left.svg" alt="" className="h-5 w-5" />
                  {tNav('back')}
                </Button>

                <Button
                  onClick={handleSaveAndContinue}
                  disabled={!hasAnyCity}
                  className={`ml-auto flex h-11 items-center justify-center gap-2 rounded-full px-5 text-sm font-medium transition-all ${
                    hasAnyCity
                      ? 'bg-gradient-to-r from-[#F1656A] via-[#ED2891] to-[#42419A] text-white hover:opacity-90'
                      : 'cursor-not-allowed bg-[#f9f9f9] text-[#bbbbbb]'
                  }`}
                >
                  <span className="whitespace-nowrap">{t('saveAndContinue')}</span>
                </Button>
              </div>
            </div>
          </div>
        </main>
      </div>
      <ConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={closeDeleteDialog}
        onConfirm={confirmDeleteCity}
        title={tDeleteCityDialog('title')}
        message={tDeleteCityDialog('message')}
        confirmText={tDeleteCityDialog('confirm')}
        cancelText={tDeleteCityDialog('cancel')}
        confirmButtonVariant="danger"
        isLoading={deleteCityMutation.isPending}
        errorMessage={deleteError ?? undefined}
      />
    </>
  );
}
